/**
 * ExitIntentSampleModal
 *
 * Quiet, non-aggressive exit-intent overlay shown on the homepage.
 *
 * Rules — designed to feel respectful, not spammy:
 *   1. Only triggers when the cursor leaves the viewport via the TOP edge
 *      (the universal "user is about to close the tab / hit back" signal).
 *      Bottom + side exits are intentionally ignored.
 *   2. Fires at most ONCE per session (sessionStorage flag).
 *   3. Honors a 90-day "dismissed" cookie in localStorage so dismissals
 *      aren't re-prompted on every visit.
 *   4. Only arms after the user has scrolled at least 40% down the page —
 *      i.e. they've engaged with content, not just landed and bounced.
 *   5. Desktop-only. Mobile UAs have no reliable mouse-leave semantics
 *      and forced modals on mobile feel intrusive.
 *
 * Telemetry (consent-gated via lib/analytics):
 *   - exit_intent_modal_shown { source: "home" }
 *   - exit_intent_modal_dismissed { source: "home", reason: "x_close"|"backdrop"|"esc"|"stay" }
 *   - exit_intent_modal_cta_clicked { source: "home", cta_target: "sample" }
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import {
    ArrowUpRight,
    X,
    Clock,
    FileText,
    Eye,
    ShieldCheck,
} from "lucide-react";
import { track } from "@/lib/analytics";
import { useAuth } from "@/lib/auth";

const SESSION_FLAG = "bl_exit_intent_shown_v1";
const VARIANT_KEY = "bl_exit_intent_variant";
const VARIANT_NAME = "sample";
const DISMISSED_AT_KEY = "bl_exit_intent_dismissed_at_v1";
const DISMISSED_TTL_MS = 90 * 24 * 60 * 60 * 1000; // 90 days

function isMobileUA() {
    if (typeof navigator === "undefined") return false;
    return /Android|iPhone|iPad|iPod|webOS|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent,
    );
}

function recentlyDismissed() {
    try {
        const raw = localStorage.getItem(DISMISSED_AT_KEY);
        if (!raw) return false;
        const ts = Number(raw);
        if (!Number.isFinite(ts)) return false;
        return Date.now() - ts < DISMISSED_TTL_MS;
    } catch {
        return false;
    }
}

/** Lazily assign or read the per-session A/B variant. 50/50 split between
 *  this Sample modal and the Buyer's-Checklist exit-intent variant. */
function getOrAssignVariant() {
    try {
        const existing = sessionStorage.getItem(VARIANT_KEY);
        if (existing === "sample" || existing === "checklist") return existing;
        const v = Math.random() < 0.5 ? "sample" : "checklist";
        sessionStorage.setItem(VARIANT_KEY, v);
        return v;
    } catch {
        return "sample";
    }
}

export default function ExitIntentSampleModal({ source = "home" }) {
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();
    const armedRef = useRef(false);
    const shownRef = useRef(false);
    // Hide the modal from signed-in users — the 60-second sample preview
    // exists to convert anonymous visitors and new (unauthenticated)
    // sign-ups. Existing customers don't need to be shown a sample of
    // the product they already use.
    const { user } = useAuth();

    useEffect(() => {
        if (typeof window === "undefined") return undefined;
        if (user) return undefined;  // signed-in user — suppress
        if (isMobileUA()) return undefined;
        // A/B with the Buyer's-Checklist exit-intent variant — only fire
        // when this session's coin flip picked us. Mutually exclusive.
        if (getOrAssignVariant() !== VARIANT_NAME) return undefined;
        try {
            if (sessionStorage.getItem(SESSION_FLAG) === "1") return undefined;
        } catch { /* non-critical */ }
        if (recentlyDismissed()) return undefined;

        // Arm only after user has scrolled at least 40% of the document.
        const handleScroll = () => {
            const doc = document.documentElement;
            const max = doc.scrollHeight - doc.clientHeight;
            if (max <= 0) return;
            const pct = window.scrollY / max;
            if (pct >= 0.4) armedRef.current = true;
        };

        const handleMouseLeave = (e) => {
            // Only top-edge exits (user heading toward tab bar / back button).
            if (e.clientY > 0) return;
            if (!armedRef.current) return;
            if (shownRef.current) return;
            shownRef.current = true;
            try {
                sessionStorage.setItem(SESSION_FLAG, "1");
            } catch { /* non-critical */ }
            track("exit_intent_modal_shown", { source });
            setOpen(true);
        };

        window.addEventListener("scroll", handleScroll, { passive: true });
        document.addEventListener("mouseleave", handleMouseLeave);
        // Initial scroll-position check (in case user already scrolled).
        handleScroll();

        return () => {
            window.removeEventListener("scroll", handleScroll);
            document.removeEventListener("mouseleave", handleMouseLeave);
        };
    }, [source, user]);

    const closeWith = (reason) => {
        track("exit_intent_modal_dismissed", { source, variant: VARIANT_NAME, reason });
        try {
            localStorage.setItem(DISMISSED_AT_KEY, String(Date.now()));
        } catch { /* non-critical */ }
        setOpen(false);
    };

    const handleCta = () => {
        track("exit_intent_modal_cta_clicked", {
            source,
            variant: VARIANT_NAME,
            cta_target: "sample",
        });
        try {
            localStorage.setItem(DISMISSED_AT_KEY, String(Date.now()));
        } catch { /* non-critical */ }
        setOpen(false);
        navigate("/sample-analysis");
    };

    return (
        <Dialog
            open={open}
            onOpenChange={(o) => {
                if (!o) closeWith("backdrop");
            }}
        >
            <DialogContent
                className="max-w-md w-[94vw] p-0 gap-0 bg-bl-surface border-bl rounded-2xl overflow-hidden [&>button.absolute]:hidden"
                data-testid="exit-intent-modal"
                onEscapeKeyDown={(e) => {
                    e.preventDefault();
                    closeWith("esc");
                }}
            >
                <VisuallyHidden.Root>
                    <DialogTitle>
                        Got 60 seconds? See what your inspection report would
                        look like
                    </DialogTitle>
                    <DialogDescription>
                        Quick preview of a Buyer's Leverage sample inspection
                        analysis. No signup required.
                    </DialogDescription>
                </VisuallyHidden.Root>
                <div className="relative px-6 pt-7 pb-3">
                    <button
                        type="button"
                        onClick={() => closeWith("x_close")}
                        aria-label="Close"
                        className="absolute top-3 right-3 w-8 h-8 rounded-full hover:bg-bl-bg flex items-center justify-center text-bl-muted hover:text-bl transition-colors"
                        data-testid="exit-intent-close-btn"
                    >
                        <X className="w-4 h-4" />
                    </button>
                    <div className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase text-bl-primary font-medium">
                        <Clock className="w-3.5 h-3.5" />
                        60-second preview
                    </div>
                    <h3
                        className="mt-2.5 font-display font-semibold text-bl text-[1.5rem] leading-tight tracking-tight"
                        data-testid="exit-intent-headline"
                    >
                        Got 60 seconds?{" "}
                        <span className="font-italic-accent text-bl-primary">
                            See what your report would look like.
                        </span>
                    </h3>
                    <p className="mt-3 text-sm text-bl-muted leading-relaxed">
                        Before you go — take one quick look at a finished
                        Buyer's Leverage sample report. Same structure your
                        inspection would produce. No signup.
                    </p>
                </div>

                <div className="px-6 py-4 bg-bl-teal-tint/30 border-y border-bl-primary/15">
                    <ul className="space-y-2 text-[13px] text-bl">
                        <li className="flex items-start gap-2">
                            <FileText className="w-4 h-4 text-bl-primary mt-0.5 shrink-0" />
                            <span>Prioritized issues, severity-tagged</span>
                        </li>
                        <li className="flex items-start gap-2">
                            <Eye className="w-4 h-4 text-bl-primary mt-0.5 shrink-0" />
                            <span>
                                Repair exposure ranges + negotiation prompts
                            </span>
                        </li>
                        <li className="flex items-start gap-2">
                            <ShieldCheck className="w-4 h-4 text-bl-primary mt-0.5 shrink-0" />
                            <span>100% redacted · no real property data</span>
                        </li>
                    </ul>
                </div>

                <div className="px-6 py-5 flex flex-col gap-2">
                    <button
                        type="button"
                        onClick={handleCta}
                        className="bl-btn-primary w-full"
                        data-testid="exit-intent-cta-btn"
                    >
                        View the sample report
                        <ArrowUpRight className="w-4 h-4" />
                    </button>
                    <button
                        type="button"
                        onClick={() => closeWith("stay")}
                        className="text-[12px] text-bl-muted hover:text-bl py-2"
                        data-testid="exit-intent-stay-btn"
                    >
                        No thanks, I'll keep browsing
                    </button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
